/* The end-of-game panels: what each player earned, fielded, researched and killed, round by round.

   StarCraft's post-game graphs (owner, 2026-09-22). Four series, one line per player, the round
   on the x axis.

   **Food is normalised to one body's upkeep**, per side. A horde making 1,800 meat at 2 a zombie
   and a humanity making 1,200 food at 3 a soldier are not comparable as raw numbers and are
   perfectly comparable as "bodies this feeds" — 900 against 400 — which is the thing worth
   drawing. Meat and food are different currencies; hunger is the same fact.

   **Troop power is the same trick played on the army**, and it took the food-eaten panel's place
   (owner, 2026-09-22): a soldier and a zombie are 1, and every other unit is what it COSTS
   against that body — a tank is 25, a titan 26.7. The server computes it
   (`server_config.TROOP_POWER_PER_UNIT`, summed in `games/history.build_summary`) and the client
   draws the number it is sent, because a number on screen is a claim about what the server will
   do. It counts the marches as well as the garrisons, so the line does not fall off a cliff the
   round a player attacks.

   Every figure is read from `GameStep.summary`, which the server records with the same functions
   the ROUND uses (`harvest_of` / `rot_of`, `upkeep_of`, `research_income_of` — see
   `games/history.build_summary`). `metadata` is pruned outside the retention window and `summary`
   is kept for the life of the game, so a graph drawn off the map would lose a long game's
   opening. `manage.py game_report` prints these same four series from the same summaries.

   Kills are the one cumulative figure: `kills_score` is a running total, so a round's kills are
   the gap between one summary and the next, floored at zero (a step can be missing). */

const basicUpkeepFor = (gameConfig, team) => {
  const troops = team === "human" ? gameConfig?.human_config?.TROOPS : gameConfig?.zombie_config?.TROOPS
  const body = team === "human" ? troops?.soldier : troops?.zombie
  return body?.CONSUMPTION || 1
}

export const GRAPHS = [
  { key: "production", titleKey: "graphs.production", unitKey: "graphs.inBodies" },
  { key: "troopPower", titleKey: "graphs.troopPower", unitKey: "graphs.inBasicBodies" },
  { key: "research", titleKey: "graphs.research", unitKey: "graphs.perRound" },
  { key: "kills", titleKey: "graphs.kills", unitKey: "graphs.perRound" },
]

/* One entry per seat: its label, its team, and the four arrays indexed by the same `ticks`.
   Seats with no summary anywhere (a pruned or fogged game) come back with empty arrays, which is
   what lets the modal say "no numbers" rather than draw an empty axis. */
export const seriesFrom = ({ steps, players, gameConfig }) => {
  const rows = (steps || []).filter((step) => step && step.summary)
  const ticks = rows.map((step) => step.tick)
  // Two shapes reach this, and both are real: the store keeps a seat camelCased with its user
  // nested (`{ user: { id, username }, team }`) while `GET /game/{id}/steps/` sends it flat
  // (`{ id, username, team }`). Reading only one of them is why the panel first drew nothing at
  // all — every seat id came back `undefined` and every series was zeros.
  const seats = (players || []).map((player) => {
    const id = String(player.user?.id ?? player.user_id ?? player.userId ?? player.id)
    const team = player.team
    const perBody = basicUpkeepFor(gameConfig, team) || 1
    const series = { production: [], troopPower: [], research: [], kills: [] }
    let previousKills = null
    rows.forEach((step) => {
      const score = step.summary?.scores?.[id] || {}
      series.production.push(Math.round((score.food_production || 0) / perBody))
      series.troopPower.push(Math.round(score.troop_power || 0))
      series.research.push(score.research_points || 0)
      const killed = score.kills_score || 0
      series.kills.push(previousKills == null ? 0 : Math.max(0, killed - previousKills))
      previousKills = killed
    })
    return {
      id,
      team,
      name: player.user?.username || player.username || player.name || `#${id}`,
      series,
    }
  })
  const hasNumbers = seats.some((seat) =>
    Object.values(seat.series).some((values) => values.some((value) => value > 0))
  )
  return { ticks, seats, hasNumbers }
}

export default seriesFrom
